import { Component, Input, OnInit } from '@angular/core';
import { LotPermissions } from '@app/core/permissions';
import { ProjectReleaseStagePermissions } from '@app/core/permissions/project-release-stage.permissions';
import { NavigationService } from '@app/core/services/navigation/navigation.service';
import { ILotSpecMappedItem } from '@app/logic/lot-spec/interfaces/i.lot-spec.mapped';
import { LotsLogicService, ILotMappedItem } from '@app/logic/lots';
import { IReleaseStageMappedItem } from '@app/logic/release-stages/interfaces/i.release-stage.mapped';
import { ReleaseStagesLogicService } from '@app/logic/release-stages/release-stages.logic.service';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import {
    ILotSwitchReleaseStageDialogData,
    LotSwitchReleaseStageDialogComponent
} from '@app/views/lot/shared/lot-switch-release-stage-dialog/lot-switch-release-stage-dialog.component';
import {
    DESIGN_SCHEME_STATUS_ENUM,
    IProjectReleaseLotDesignSummaryDto,
    IProjectReleaseLotDto, IProjectReleaseStageDto, LotTypeEnumId,
    LOT_CONTRACT_TYPE_ENUM, LOT_JOB_STATUS_ENUM,
    LOT_TYPE_ENUM, MARKET_STATUS_ENUM
} from '@classictechsolutions/hubapi-transpiled-enums';
import { ProjectReleaseStageEditLotDialogComponent } from '../summary/edit/edit-lot-dialog.component';
import { IRequestConceptImagesDialogData, RequestConceptImagesDialogComponent } from './dialogs/request-concept-images/request-concept-images.component';
import { SelectLotTemplateDialogComponent } from './dialogs/select-lot-template-dialog/select-lot-template-dialog.component';
import { take } from 'rxjs';

export interface ICustomProjectReleaseLotDesignSummaryDto extends IProjectReleaseLotDesignSummaryDto {
    $shouldRequestDesignScheme: boolean;
    $shouldApplyConcept: boolean;
}

@Component({
    selector: 'cb-manage-project-release-stage-lots-design-tab',
    templateUrl: './release-lots-design.component.html',
    styleUrls: ['./release-lots-design.component.scss'],
    standalone: false
})
export class ManageReleaseLotsDesignTabComponent implements OnInit {
    @Input() public mappedItem: IReleaseStageMappedItem;
    @Input() public projectReleases: IProjectReleaseStageDto[];
    public selectedTabIndex = 0;
    public lots: ICustomProjectReleaseLotDesignSummaryDto[];
    public loaded = false;
    public MARKET_STATUS_ENUM = MARKET_STATUS_ENUM;
    public LOT_JOB_STATUS_ENUM = LOT_JOB_STATUS_ENUM;
    public LOT_CONTRACT_TYPE_ENUM = LOT_CONTRACT_TYPE_ENUM;
    public LOT_TYPE_ENUM = LOT_TYPE_ENUM;
    public DESIGN_SCHEME_STATUS_ENUM = DESIGN_SCHEME_STATUS_ENUM;
    public spec: ILotSpecMappedItem;
    constructor(
        private readonly releaseStagesLogicService: ReleaseStagesLogicService,
        public readonly lotPermissions: LotPermissions,
        public readonly projectReleaseStagePermissions: ProjectReleaseStagePermissions,
        public readonly cbDialog: CbDialogService,
        private readonly navigationService: NavigationService,
        private readonly lotsLogicService: LotsLogicService,
    ) { }

    public ngOnInit(
    ): void {
        this.loadLots();
    }

    public editLot(lot: IProjectReleaseLotDto): void {
        this.lotsLogicService.$getMappedItem(lot.id).pipe(take(1)).subscribe(LotMappedItem => {
            this.cbDialog
                .open(ProjectReleaseStageEditLotDialogComponent, {
                    data: { mappedItem: LotMappedItem, isReadonly: false },
                    minWidth: '80%',
                })
                .afterClosed()
                .pipe(take(1)).subscribe((result: ILotMappedItem) => {
                    if (result) {
                        const index = result?.id != null && this.lots.findIndex(x => x.id === result.id);
                        if (index >= 0) {
                            Object.assign(this.lots[index], result);
                        }
                    }
                });
        });
    }

    public viewLotDetails(lot: IProjectReleaseLotDto): void {
        this.lotsLogicService.$getMappedItem(lot.id).pipe(take(1)).subscribe(LotMappedItem => {
            this.cbDialog
                .open(ProjectReleaseStageEditLotDialogComponent, {
                    data: { mappedItem: LotMappedItem, isReadonly: true },
                    minWidth: '80%',
                })
                .afterClosed()
                .pipe(take(1)).subscribe();
        });
    }

    public viewLot(lot: IProjectReleaseLotDto): void {
        this.navigationService.navigate([`lots/${lot.id}/summary`]);
    }

    public getAmenitiesDisplay(noAmenities: number, lotType: LotTypeEnumId): string {
        return this.isBlockLotType(lotType) ? '-' : noAmenities.toString();
    }

    public isBlockLotType(value: LotTypeEnumId): boolean {
        return value === LOT_TYPE_ENUM.ApartmentBlock || value === LOT_TYPE_ENUM.TerracedBlock;
    }

    public canRequestRender(lot: ICustomProjectReleaseLotDesignSummaryDto): boolean {
        return !lot.hasClientSale && lot.hasConceptRevisions;
    }

    public canEditLot(lot: ICustomProjectReleaseLotDesignSummaryDto): boolean {
        return !lot.hasSchemes;
    }

    public canSelectSpecTemplate(lot: ICustomProjectReleaseLotDesignSummaryDto): boolean {
        return !lot.hasClientSale && !lot.specTemplateId && !lot.isParent;
    }

    public applyTemplate(lot: ICustomProjectReleaseLotDesignSummaryDto): void {
        this.cbDialog
            .open(SelectLotTemplateDialogComponent, {
                data: { lotId: lot.id },
                minWidth: '1000px',
            })
            .afterClosed()
            .pipe(take(1)).subscribe(result => {
                if (result) {
                    this.loadLots();
                }
            });
    }

    public switchReleaseStage(lot: ICustomProjectReleaseLotDesignSummaryDto): void {
        this.lotsLogicService.$getMappedItem(lot.id).pipe(take(1)).subscribe(mappedItem => {
            this.cbDialog
                .open<ILotSwitchReleaseStageDialogData>(
                LotSwitchReleaseStageDialogComponent,
                {
                    data:
                        {
                            mappedItem
                        }
                })
                .afterClosed()
                .pipe(take(1)).subscribe(result => {
                    if (result) {
                        this.loadLots();
                    }
                });
        });
    }

    public requestRender(lot: ICustomProjectReleaseLotDesignSummaryDto): void {
        this.cbDialog.simpleMessageDialog('Request Render is Not yet Implemented');
    }

    public requestFloorPlan(lot: ICustomProjectReleaseLotDesignSummaryDto): void {
        this.cbDialog.simpleMessageDialog('Request Floor Plan is Not yet Implemented');
    }

    public canRequestFloorPlan(lot: ICustomProjectReleaseLotDesignSummaryDto): boolean {
        return !lot.hasClientSale && lot.hasConceptRevisions;
    }

    public canSwitchReleaseStage(lot: IProjectReleaseLotDto): boolean {
        return !lot.hasClientSale && this.projectReleases?.length > 1;
    }

    private loadLots = (): void => {
        this.loaded = false;
        this.releaseStagesLogicService.getProjectReleaseStageDesignSummaryLots(this.mappedItem.id).pipe(take(1)).subscribe((result) => {
            this.lots = result;
            this.loaded = true;
        });
    };

    public requestImagesDialog(): void {
        this.cbDialog
            .open<IRequestConceptImagesDialogData, boolean>(
            RequestConceptImagesDialogComponent,
            {
                data: { lots: this.lots },
                minWidth: '1000px',
            }
        )
            .afterClosed()
            .pipe(take(1)).subscribe(result => {
                if (result) {
                    this.loadLots();
                }
            });
    }
}
